import * as React from 'react'
import { FlatList, Text, TouchableOpacity, View } from 'react-native'
import styled from '@theme/styled-components'

import Checkbox from '@components/checkbox/Checkbox'
import Icon from '@components/icon/Icons'
import Tag from '@components/tag/Tag'

import { FormikHelpers, useFormik } from 'formik'

import useI18n from '@i18n/useI18n'
import useReducer from '@store/useReducer'
import * as LocationStore from '@store/location/location'
import * as SocieteStore from '@store/societe/societe'
import * as SiteStore from '@store/site/site'
import * as MetierStore from '@store/metier/metier'

import { findLocalisationParents } from '@screens/incident/utils'

import useBreakpoint from '@utils/useBreakpoints'

interface Values {
  localisationIds: string[]
}

const ITEM_WIDTH = 360

const Space = ({ form, onForm, onQuit }: ControleFormProps) => {
  const i18n = useI18n()
  const [{ width }, bp] = useBreakpoint()

  const init: Values = React.useMemo(() => ({ localisationIds: form.localisationIds }), [form.localisationIds])

  const [opens, setOpens] = React.useState<string[]>([])

  const locationTree = useReducer(LocationStore.store, (s) => s.tree)
  const places = useReducer(LocationStore.store, (s) => s.places)
  const services = useReducer(SocieteStore.store, (s) => s.services)
  const manifestations = useReducer(SiteStore.store, (s) => s.manifestations)
  const metiers = useReducer(MetierStore.store, (s) => s.metiers)

  const onSubmit = (vals: Values, { setSubmitting }: FormikHelpers<Values>) => {
    const curReponses = form.reponses.reduce((acc, cur) => {
      return acc.some((val) => val.questionId === cur.questionId) ? acc : [...acc, cur]
    }, [] as ControleResponseForm[])

    const reponses =
      form.answerMode === 'allSpaces'
        ? vals.localisationIds.reduce(
            (acc, cur) => [...acc, ...curReponses.map((r) => ({ ...r, localisationId: cur }))],
            [] as ControleResponseForm[]
          )
        : form.reponses && form.reponses.filter((r) => vals.localisationIds.includes(r.localisationId)) // remove responses for removed locations

    const next: Partial<ControleForm> = {
      ...vals,
      reponses,
    }

    onForm(next).finally(() => setSubmitting(false))
  }

  const { values, setFieldValue, handleSubmit, dirty } = useFormik<Values>({
    enableReinitialize: true,
    initialValues: init,
    onSubmit,
  })

  React.useEffect(() => {
    if (dirty) {
      // sumbit on change
      handleSubmit()
    }
  }, [values])

  const onOpen = (id: string, open: boolean) => {
    if (open) {
      setOpens(opens.filter((o) => o !== id))
    } else {
      setOpens([...opens, id])
    }
  }

  const onSelect = (location: LocalisationResponse, ids: string[], selected: boolean) => {
    const parents = findLocalisationParents(places, location).map((l) => l.id)

    if (selected) {
      // remove every parents and the location
      const next = values.localisationIds.filter((l) => l !== location.id && !parents.includes(l) && !ids.includes(l))
      setFieldValue('localisationIds', next)
    } else {
      const next = [...values.localisationIds, ...ids, location.id]

      // check if every children are completed
      const completed = parents.reverse().reduce((acc, id) => {
        const found = places
          .filter((p) => p.enabled)
          .filter((p) => p.parentId === id)
          .every((p) => next.includes(p.id) || acc.includes(p.id))

        if (found) {
          acc.push(id)
        }

        return acc
      }, [] as string[])

      setFieldValue('localisationIds', Array.from(new Set([...completed, ...next])))
    }
  }

  const renderLocation = (item: LocationTreeItem) => {
    const open = opens.includes(item.location.id)
    const selected = values.localisationIds.includes(item.location.id)
    const withSubSelected = item.subLevelIds.some((l) => values.localisationIds.includes(l))

    return (
      <React.Fragment key={item.location.id}>
        <ItemHeader>
          <ItemOpen activeOpacity={0.8} onPress={() => onOpen(item.location.id, open)}>
            <IconContainer open={open}>{item.subs.length > 0 && <Icon name="arrow-down" size={20} />}</IconContainer>
            <ItemText>{item.location.libelle}</ItemText>
          </ItemOpen>

          <ItemSelect>
            <Checkbox
              onClick={() => onSelect(item.location, item.subLevelIds, selected)}
              label=""
              value={selected}
              partial={withSubSelected}
              iconOnTheRight
            />
          </ItemSelect>
        </ItemHeader>
        {!!open && <Subs>{item.subs.map(renderLocation)}</Subs>}
      </React.Fragment>
    )
  }

  const renderSpaceCard = ({ item }: { item: LocationTreeItem }) => {
    return <Card>{renderLocation(item)}</Card>
  }

  const columns = bp === 'phone' ? 1 : Math.floor((width - 300) / ITEM_WIDTH)

  const societe = React.useMemo(() => services.find((s) => s.id === form.societeId), [services])
  const metier = React.useMemo(() => metiers.find((m) => m.id === form.metierId), [metiers])

  const manif = React.useMemo(
    () =>
      [...manifestations.inProgress, ...manifestations.incoming, ...manifestations.toBeClosed].find(
        (m) => m.id === form.manifestationId
      ),
    [manifestations]
  )

  const manifLocationTree = React.useMemo(
    () =>
      (manif ? locationTree.filter((space) => manif.espaceIds.includes(space.location.id)) : locationTree).map(
        // Désactivation de la fonctionnalité de sélection de sous-niveaux
        (loc) => ({ ...loc, subLevelIds: [], subs: [] })
      ),
    [locationTree, manif]
  )

  const title = React.useMemo(
    () => [societe ? societe.nameSociete : '', metier ? metier.libelle : ''].filter(Boolean).join(' - '),
    [societe, metier]
  )

  const subtitle = React.useMemo(
    () =>
      [manif ? manif.libelle : '', form.period ? i18n.t(`screens.controles.form.period.${form.period}`) : undefined]
        .filter(Boolean)
        .join(' - '),
    [manif, form.period]
  )

  const renderHeader = () => {
    return (
      <>
        <Header>
          {bp !== 'phone' && (
            <TagContainer>
              <Tag
                activeColor
                focused
                item={{ label: i18n.t('screens.controles.form.quit'), value: 'tag' }}
                selected={false}
                leftIcon="close"
                onItemSelected={onQuit}
              />
            </TagContainer>
          )}

          <TitleContainer>
            {!!title && <Title>{title}</Title>}
            {!!subtitle && <SubTitle>{subtitle}</SubTitle>}
          </TitleContainer>
        </Header>

        <Info>{i18n.t('screens.controles.form.spaces.uncheck')}</Info>
      </>
    )
  }

  return (
    <MainContainer>
      <FlatList
        key={columns}
        data={manifLocationTree}
        keyExtractor={(item) => item.location.id}
        renderItem={renderSpaceCard}
        numColumns={columns}
        ListHeaderComponent={renderHeader}
      />
    </MainContainer>
  )
}

export default Space

const MainContainer = styled(View)`
  flex: 1;
`

const Card = styled(View)`
  background-color: ${(props) => props.theme.colors.background};
  border-radius: 8px;
  margin: 8px ${(props) => (props.theme.breakpoint === 'phone' ? 16 : 0)}px;
  ${(props) => props.theme.breakpoint !== 'phone' && 'margin-left: 24px'};
  ${(props) => props.theme.breakpoint !== 'phone' && `width: ${ITEM_WIDTH}px;`}
  ${(props) => props.theme.breakpoint !== 'phone' && 'align-self: flex-start;'}
  ${(props) => props.theme.constants.shadow};
`

const IconContainer = styled(View)<{ open: boolean }>`
  width: 30px;
  align-items: center;
  justify-content: center;
  transform: ${(props) => (props.open ? 'rotate(0deg)' : 'rotate(-90deg)')};
`

const Subs = styled(View)`
  margin-left: 16px;
`

const ItemHeader = styled(View)`
  flex-direction: row;
  align-items: center;
  padding: 16px 0;
  border: 0 solid ${(props) => props.theme.colors.backgroundTertiary};
  border-bottom-width: 1px;
`

const ItemOpen = styled(TouchableOpacity)`
  flex: 1;
  flex-direction: row;
  align-items: center;
`

const ItemSelect = styled(View)`
  padding: 0 10px;
`

const ItemText = styled(Text)`
  ${(props) => props.theme.fonts.Body};
`

const Info = styled(Text)`
  ${(props) => props.theme.fonts.Body};
  margin: ${(props) => (props.theme.breakpoint === 'phone' ? 16 : 24)}px;
`

const Header = styled(View)`
  background-color: ${(props) =>
    props.theme.breakpoint === 'phone' ? props.theme.colors.primary : props.theme.colors.backgroundSecondary};
  padding: ${(props) => (props.theme.breakpoint === 'phone' ? 16 : 24)}px;
`

const TitleContainer = styled(View)`
  background-color: ${(props) => props.theme.colors.background};
  padding: ${(props) => (props.theme.breakpoint === 'phone' ? 12 : 24)}px;
  align-items: center;
  border-radius: 8px;
`

const Title = styled(Text)`
  ${(props) => props.theme.fonts.Title3};
  font-size: 22px;
  text-align: center;
`

const SubTitle = styled(Text)`
  ${(props) => props.theme.fonts.Body};
  color: ${(props) => props.theme.colors.secondaryText};
  text-align: center;
`

const TagContainer = styled(View)`
  align-self: flex-start;
  margin-bottom: 24px;
`
